import { Money } from '@arema/components';
import { EventDate, EventDateData, SetLoading } from '@arema/components/Classes';
import { useAPI, useValidator } from '@arema/components/Hooks';
import { bindClick, fromUnix, getNow } from '@arema/components/Util';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Header, Icon, Message, Modal, Table } from 'react-frontier2';
import { Trans, useTranslation } from 'react-i18next';
import { firstBy } from 'thenby';
import API from '../API';

interface UpdateTicketDateModalProps{
	ticketId: number,
	eventId: number,
	dateId: number,
	sectionId: number,
	priceId: number,
	title?: string,
	open: boolean,
	onClose: ()=>void,
	onSave: (ticket_id: number, date: EventDateData, section: EventDateData["sections"][0], price: EventDateData["sections"][0]["prices"][0])=>void,
}

const PAGE_SIZE = 20;
var UpdateTicketDateModal = (props: UpdateTicketDateModalProps)=>{
	var { t } = useTranslation();
	var [page, setPage] = useState<number>(0);
	var [loadError, setLoadError] = useState<string>(null);
	var [dates, setDates] = useState<{
		event_id: number,
		dates: EventDate[]
	}>(null);
	var { data: dateData, load: loadDate, loadError: dateLoadError } = useAPI(API.getDate);
	var { data, onDataChange, validate, reset, setData, prompts, setPrompts } = useValidator({
		price_id: null,
		date_id: null,
		section_id: null,
	}, {
		price_id: ['number'],
		date_id: ['number'],
		section_id: ['number'],
	});

	useEffect(()=>{
		if(!props.open) return;
		if(!props.ticketId || !props.eventId) return;
		if(!dates || dates.event_id!==props.eventId) return loadDates();
	}, [props.open, props.dateId, props.sectionId, props.priceId, props.eventId, props.ticketId]);

	var loadDates = ()=>{
		setPage(0);
		setDates(null);
		API.getEventDates(props.eventId).then(res=>{
			if(res.error) return setLoadError(res.message);
			setDates({
				event_id: props.eventId,
				dates: res.data
			});
		}).catch(err=>{
			setLoadError(t('common.unexpected_code', { code: 'LCL-UPTKDT-1' }));
		});
	}

	useEffect(()=>{
		if(!data?.date_id || dateData && dateData.date_id===data?.date_id) return;
		loadDate(data.date_id, ['sections', 'prices'], ['delivery', 'payment_methods', 'venue']);
	}, [data?.date_id]);

	var onSubmit = (setLoading: SetLoading)=>{
		var { valid } = validate();
		if(!valid) return;

		setLoading(true);
		API.changeTicketPrice(props.ticketId, data.price_id).then(res=>{
			if(res.error) return setPrompts([res.message]);
			return props.onSave(props.ticketId, dateData, selected_section, selected_price);
		}).catch(err=>{
			return setPrompts([t('common.unexpected_code', { code: 'LCL-UPTKDT-2' })]);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var onDateSelected = (date_id: number)=>{
		return ()=>{
			setData({
				date_id,
				price_id: null,
				section_id: null
			});
		}
	}

	var onSectionSelected = (section: EventDateData["sections"][0])=>{
		return ()=>{
			setData({
				date_id: data.date_id,
				price_id: null,
				section_id: section.section_id,
			});
		}
	}

	var onPriceSelected = (price: EventDateData["sections"][0]["prices"][0])=>{
		return ()=>{
			setData({
				date_id: data.date_id,
				price_id: price.price_id,
				section_id: data.section_id
			});
		}
	}

	var DatesList = useMemo(()=>{
		if(!dates) return [];
		var now = getNow().toSeconds();
		var sorted_dates = dates.dates.sort(firstBy((a: EventDate, b: EventDate)=>{
			return (a.date>=now ? 0 : 1) - (b.date>=now ? 0 : 1)
		}).thenBy((a: EventDate, b: EventDate)=>{
			return a.date>=now ? (a.date-b.date) : (b.date-a.date);
		}));
		
		var date_slice = sorted_dates.slice(page*PAGE_SIZE, (page*PAGE_SIZE)+PAGE_SIZE);
		var items : React.ReactElement[] = [];
		var is_past = false;
		for(var a of date_slice){
			if(a.date<=now && !is_past){
				is_past = true;
				items.push((
					<Table.Row header selectable={false} key={`PSTHDR`}>
						<Table.Cell empty colSpan={100} style={{ fontWeight: 'bold' }}>
							{t('select_date_modal.past_dates')}
						</Table.Cell>
					</Table.Row>
				))
			}
			items.push((
				<Table.Row key={`DTSL-${a.date_id}`} selectable collapsingIndexes={[0, 1, 3]} centeredIndexes={[0]} onClick={onDateSelected(a.date_id)} data={[
					<Icon name={props.dateId===a.date_id ? 'circle-check' : 'circle'} solid={props.dateId===a.date_id} fitted />,
					a.date_id,
					fromUnix(a.date).toFormat('dd/MMM/yy HH:mm'),
					a.city,
				]} />
			))
		}
		return items;
	}, [dates, page, props.dateId]);

	var selected_section = useMemo(()=>{
		if(!data?.section_id || !dateData) return null;
		return dateData.sections.find(a=>a.section_id===data.section_id);
	}, [data?.section_id, data?.date_id]);
	var selected_price = useMemo(()=>{
		if(!data?.price_id || !dateData || !data?.section_id) return null;
		var sc = dateData.sections.find(a=>a.section_id===data.section_id);
		if(!sc) return null;
		return sc.prices.find(a=>a.price_id===data.price_id);
	}, [data?.date_id, data?.section_id, data?.price_id]);

	return <>
		<Modal open={props.open} onClose={props.onClose} size={'tiny'}>
			<Modal.Header text={props.title || t('modals.move_ticket_date.title')} />
			<Modal.Content>
				{!dates ? (
					<Header loading text={t('common.loading')} />
				) : <>
					<Table top fitted details>
						<Table.Body>
							<Table.Row data={[t('common.event_date', { count: 1 }), (!!data?.date_id && !!dateData?.date) ? fromUnix(dateData?.date).toFormat('dd/MMM/yy HH:mm') : null]} />
							<Table.Row data={[t('common.venue_section', { count: 1 }), selected_section?.section_name]} />
							<Table.Row data={[t('common.event_price', { count: 1 }), selected_price?.price_name]} />
						</Table.Body>
					</Table>
					{!data.date_id ? (
						<Table fitted last removeBorder={['top']}>
							<Table.Body>
								{DatesList}
							</Table.Body>
						</Table>
					) : !data.section_id ? (
						!dateData ? (
							<Header loading text={t('common.loading')} />
						) : (
							<Table fitted selectable style={{ marginBottom: -20 }} removeBorder={['top']}>
								<Table.Head title={t('modals.move_ticket_date.sections.title')} data={[t('modals.move_ticket_date.sections.headers', { returnObjects: true })]} titleRight={(
									<Button text={t('common.return')} size={'tiny'} iconName={'arrow-left'} onClick={bindClick(onDataChange('date_id'), null)} />
								)} />
								<Table.Body>
									{dateData?.sections.filter(a=>!a.numbered).sort((a,b)=>a.sort_order-b.sort_order).map(a=>(
										<Table.Row
											key={`SCT-${a.section_id}`}
											collapsingIndexes={[0, 1, 3]}
											centeredIndexes={[0, 3]}
											onClick={onSectionSelected(a)}
											data={[
												<Icon name={props.sectionId===a.section_id ? 'circle-check' : 'circle'} solid={props.sectionId===a.section_id} fitted />,
												a.section_id, 
												a.section_name, 
												a.prices.length==0 ? (
													<Icon name='minus' color='gray' />
												) : <>
													<Money value={a.prices_min} hideZeroes />
													{a.prices_min<a.prices_max ? <>
														{' '} - <Money value={a.prices_max} hideZeroes />
													</> : null}
												</>
											]} 
										/>
									))}
								</Table.Body>
							</Table>
						)
					) : (!data.price_id && !!selected_section) ? (
						<Table fitted selectable style={{ marginBottom: -20 }} removeBorder={['top']}>
							<Table.Head title={t('modals.move_ticket_date.prices.title')} data={[t('modals.move_ticket_date.prices.headers', { returnObjects: true })]} titleRight={(
								<Button text={t('common.return')} size={'tiny'} iconName={'arrow-left'} onClick={bindClick(onDataChange('section_id'), null)} />
							)} />
							<Table.Body>
								{selected_section.prices.map(a=>(
									<Table.Row
										key={`EVPR-${a.price_id}`}
										collapsingIndexes={[0, 1, 3]}
										centeredIndexes={[0, 3]}
										onClick={onPriceSelected(a)}
										data={[
											<Icon name={props.priceId===a.price_id ? 'circle-check' : 'circle'} solid={props.priceId===a.price_id} fitted />,
											a.price_id, 
											a.price_name,
											<Money value={a.cost+a.commission} />
										]} 
									/>
								))}
							</Table.Body>
						</Table>
					) : (
						<Message type='info' style={{ marginTop: 15 }} centered>
							<Trans i18nKey={'modals.move_ticket_date.disclamer'} />
							<Button text={t('common.return')} color={'blue'} onClick={bindClick(onDataChange('price_id'), null)} iconName={'arrow-left'} style={{ marginTop: 10 }} />
						</Message>
					)}
				</>}
				<Message type='error' list={prompts} style={{ marginTop: 15 }} />
			</Modal.Content>
			<Modal.Actions>
				<Button text={t('common.close')} onClick={props.onClose} basic />
				<Button text={t('common.save')} onClick={onSubmit} color={'black'} disabled={!data.price_id || !data.date_id || !data.section_id} />
			</Modal.Actions>
		</Modal>
	</>
}

export default UpdateTicketDateModal;